import "reflect-metadata"

import { ApolloProvider } from "@apollo/client"
import { useAnalytics } from "@lazarentertainment/analytics-collector"
import globalLogger from "@lazarentertainment/js-logger"
import { TooltipProvider } from "@lazarentertainment/web-design-system/components/tooltip"
import { AuthProvider } from "client/components/auth"
import { ChannelProvider } from "client/components/channel"
import { LayoutProvider } from "client/components/layout"
import { FillViewportView } from "client/components/layout/FillViewportView"
import ModalLayer from "client/components/ModalLayer"
import { ParamProvider } from "client/components/params/ParamContext"
import {
  parseQueryParams,
  QueryParams,
} from "client/components/params/types"
import { ToastLayer } from "client/components/ToastLayer"
import { GetChannelDocument, PluginsDocument } from "client/graphql"
import {
  addApolloState,
  APOLLO_STATE_PROP_NAME,
  ConnectionParams,
  initApollo,
  useApollo,
} from "client/graphql/init-apollo"
import { ChatProvider } from "client/packages/chat/components/ChatContext"
import { CartProvider } from "client/packages/commerce/components/cart/CartContext"
import { ShopProvider } from "client/packages/commerce/components/ShopContext"
import { GiveawaysProvider } from "client/packages/giveaways/context/GiveawaysContext"
import { VideoProvider } from "client/packages/player/player/VideoContext"
import { StageTenAPI } from "client/packages/sdk/StageTenApi"
import { TargetProvider } from "client/packages/target/components/TargetContext"
import { GlobalProvider } from "client/packages/utils/GlobalContext"
import { VotingProvider } from "client/packages/voting/components/VotingContext"
import {
  GetServerSideProps,
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from "next"
import getConfig from "next/config"
import dynamic from "next/dynamic"
import Error from "next/error"
import { useRouter } from "next/router"
import { useEffect } from "react"
import { getChannelSlugLookupService } from "server/slug-lookup/channel-slug-lookup.service"

const EmbedView = dynamic(
  () => import("../../../src/client/components/main/EmbedView"),
  { ssr: false },
)

interface Props {
  errorCode?: number
  channelId?: string
  params: QueryParams
}

export const getServerSideProps: GetServerSideProps<Props> = (async (
  ctx: GetServerSidePropsContext,
) => {
  const { query } = ctx
  const slug: string = query.slug as string

  try {
    const channelsService = getChannelSlugLookupService()

    const channelId = await channelsService.findChannelIdForSlug(slug)

    const { publicRuntimeConfig } = getConfig()

    const apolloClient = initApollo(publicRuntimeConfig, { channelId })

    await Promise.all([
      apolloClient.query({
        query: GetChannelDocument,
        variables: { channelId },
      }),
      apolloClient.query({
        query: PluginsDocument,
        variables: { channelId },
      }),
    ])

    return addApolloState(apolloClient, {
      props: { channelId, params: query },
    })
  } catch (err) {
    globalLogger.error(err)
    return { props: { params: query, errorCode: 404 } }
  }
}) satisfies GetServerSideProps<Props>

export default function Embed(
  pageProps: Props &
    InferGetServerSidePropsType<typeof getServerSideProps>,
) {
  const router = useRouter()
  const { analytics } = useAnalytics()
  const { publicRuntimeConfig } = getConfig()

  const { channelId, errorCode } = pageProps

  const params = parseQueryParams(router.query)
  const { apiVersion } = params

  const connectionParams: ConnectionParams = {
    channelId,
  }
  const initialState = pageProps[APOLLO_STATE_PROP_NAME]

  const client = useApollo(
    publicRuntimeConfig,
    connectionParams,
    initialState,
  )

  useEffect(() => {
    analytics.page("public", "embed", {
      channelId,
    })

    analytics.identify(undefined, {
      channelId,
    })
  }, [])

  if (errorCode) {
    return <Error statusCode={errorCode} />
  }

  return (
    <ApolloProvider client={client}>
      <AuthProvider channelId={channelId}>
        <ChannelProvider
          channelId={channelId}
          onError={() => <Error statusCode={404} />}
        >
          <ParamProvider params={router.query}>
            <VideoProvider>
              <ShopProvider>
                <VotingProvider>
                  <ChatProvider>
                    <GiveawaysProvider>
                      <CartProvider>
                        <GlobalProvider>
                          <TargetProvider>
                            <StageTenAPI
                              version={apiVersion}
                              disabled={apiVersion === undefined}
                            >
                              <TooltipProvider>
                                <LayoutProvider>
                                  <FillViewportView>
                                    <ModalLayer />
                                    <ToastLayer />
                                    <EmbedView />
                                  </FillViewportView>
                                </LayoutProvider>
                              </TooltipProvider>
                            </StageTenAPI>
                          </TargetProvider>
                        </GlobalProvider>
                      </CartProvider>
                    </GiveawaysProvider>
                  </ChatProvider>
                </VotingProvider>
              </ShopProvider>
            </VideoProvider>
          </ParamProvider>
        </ChannelProvider>
      </AuthProvider>
    </ApolloProvider>
  )
}
